import React, { useEffect, useState } from 'react';
import styles from '../stripes.module.scss';
import clsx from 'clsx';

import testIds from '@smart-tv/e2e/shared/variables';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import Typography from '@smart-tv/components/typography';

import { ContentItem, ContentLayout } from '@smart-tv/typings/content';

import formatDuration from '@smart-tv/utils/formatter/format-duration';
import formatTitle from '@smart-tv/utils/formatter/format-title';
import { useSetAtom } from 'jotai';
import { isHeroRenderedAtom } from '@smart-tv/atoms';

export interface Props {
  contentItem: ContentItem;
  universeTitle?: string;
}

function formatContentInfo({
  genre,
  year,
  duration
}: { genre?: string[]; year?: number; duration?: number } = {}): string {
  let contentInfo = '';

  let oneSet = false;

  if (genre) {
    contentInfo += genre[0];
    oneSet = true;
  }

  if (oneSet && year) {
    contentInfo += ' | ';
    oneSet = false;
  }

  if (year) {
    contentInfo += year;
    oneSet = true;
  }

  if (oneSet && duration) {
    contentInfo += ' | ';
  }

  if (duration) {
    contentInfo += formatDuration(duration);
  }

  return contentInfo;
}

export const StripesHero = (props: Props) => {
  const { contentItem, universeTitle } = props;
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const setIsHeroRendered = useSetAtom(isHeroRenderedAtom);

  useEffect(() => {
    if (setIsHeroRendered) {
      setIsHeroRendered(true);
    }
    return () => {
      if (setIsHeroRendered) {
        setIsHeroRendered(false);
      }
    };
  }, [setIsHeroRendered]);

  if (!contentItem) return null;

  const { title, duration, year, genre, heroImage, contentLayout } = contentItem;

  const contentInfo = formatContentInfo({ genre, year, duration });

  return (
    <React.Fragment>
      <div className={styles['hero']}>
        {heroImage && (
          <div className={styles['image-container']}>
            <div className={styles['image-overlay']}></div>
            <LazyLoadImage
              src={heroImage}
              alt={title ? title : ''}
              data-testid={testIds.frontPage.hero}
              onLoad={() => setIsImageLoaded(true)}
              className={clsx(styles['image'], { [styles['image--loaded']]: isImageLoaded })}
            />
          </div>
        )}
        <div
          className={clsx(styles['details'], {
            [styles['details--vertical']]: contentLayout === ContentLayout.Portrait
          })}
        >
          {universeTitle != undefined && (
            <div className={styles['universe-title-container']}>
              <Typography
                className={styles['universe-title']}
                textAlign="center"
                data-testid={testIds.frontPage.universeName}
              >
                {universeTitle}
              </Typography>
            </div>
          )}
          <Typography
            className={styles['header']}
            variant="h1"
            data-testid={testIds.frontPage.title}
          >
            {formatTitle(title)}
          </Typography>
          {contentInfo != '' && (
            <Typography
              className={styles['sub-header']}
              variant="body"
              data-testid={testIds.frontPage.info}
            >
              {contentInfo}
            </Typography>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(StripesHero);
